import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, Wallet, Sparkles, User as UserIcon, Lock, Crown, X, History } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/lib/api";
import CreditsAuditPanel from "@/components/CreditsAuditPanel";

/**
 * Admin → Customer Lookup. Find any user by email/name/phone, see their
 * loyalty + credits + sub status, and manually grant or deduct credits/points.
 *
 * If `initialEmail` prop is provided (e.g. clicked from the "All customers"
 * directory below), the card auto-fills the search, runs it, and selects the
 * matching user — saving the admin a second tap.
 */
export default function CustomerLookupCard({ initialEmail = "", bumpKey = 0 } = {}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [grantCredits, setGrantCredits] = useState("");
  const [grantPoints, setGrantPoints] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [granting, setGranting] = useState(false);
  // Link Pro admin-grant state
  const [proDays, setProDays] = useState("30");
  const [proBusy, setProBusy] = useState(false);
  // Past-fare attribution state (offline fares from JIM/cash/etc. that
  // cleared in an outside system and need to be added to this customer's
  // totals so the tax report stays accurate).
  const todayISO = new Date().toISOString().slice(0, 10);
  const [pastFareDate, setPastFareDate] = useState(todayISO);
  const [pastFareTime, setPastFareTime] = useState("");
  const [pastFareAmount, setPastFareAmount] = useState("");
  const [pastFareTip, setPastFareTip] = useState("");
  const [pastFareMethod, setPastFareMethod] = useState("jim_card");
  const [pastFareNote, setPastFareNote] = useState("");
  const [pastFareDriverId, setPastFareDriverId] = useState("");
  const [pastFareBusy, setPastFareBusy] = useState(false);
  // Approved drivers (lazy-loaded once when card mounts) for the past-fare picker
  const [approvedDrivers, setApprovedDrivers] = useState([]);
  // Loyalty history (lazy-loaded when a user is selected)
  const [loyaltyHistory, setLoyaltyHistory] = useState(null);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [loyaltyBusy, setLoyaltyBusy] = useState(false);
  // Recent bookings (lazy-loaded)
  const [trips, setTrips] = useState(null);
  const [tripsOpen, setTripsOpen] = useState(false);
  const [tripsBusy, setTripsBusy] = useState(false);
  const [tripDeletingId, setTripDeletingId] = useState(null);
  const rootRef = useRef(null);

  const search = async (query) => {
    const term = (query ?? q).trim();
    if (term.length < 2) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/search`, { params: { q: term } });
      setResults(data || []);
      // If the search was an exact-email autofill from the directory click,
      // pre-select the matching row so the admin doesn't have to tap twice.
      if (initialEmail && (data || []).length > 0) {
        const exact = data.find((u) => (u.email || "").toLowerCase() === initialEmail.toLowerCase());
        if (exact) setSelected(exact);
      }
      if ((data || []).length === 0) toast.info("No users found");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fire search + auto-select when the parent passes a customer to load.
  // `bumpKey` lets the parent retrigger this even when `initialEmail` is the
  // same (e.g., admin re-taps the same row after switching tabs).
  useEffect(() => {
    if (!initialEmail) return;
    setQ(initialEmail);
    search(initialEmail);
    // Scroll the card into view — important on mobile where the directory
    // can be far below the lookup card.
    setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail, bumpKey]);

  // Reset loyalty-history panel state whenever the selected user changes
  useEffect(() => {
    setLoyaltyHistory(null);
    setLoyaltyOpen(false);
    setTrips(null);
    setTripsOpen(false);
  }, [selected?.id]);

  // Lazy-load approved drivers once so the past-fare driver picker has options.
  // If there's only one (solo operator) we pre-select them automatically.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/drivers?status=approved`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.drivers || []);
        setApprovedDrivers(list);
        if (list.length === 1) setPastFareDriverId(list[0].id);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const submitGrant = async () => {
    if (!selected) return;
    const dollars = parseFloat(grantCredits || "0");
    const points = parseInt(grantPoints || "0", 10) || 0;
    const cents = Math.round(dollars * 100);
    if (cents === 0 && points === 0) {
      toast.error("Enter credits or points to grant (positive to add, negative to deduct)");
      return;
    }
    setGranting(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/${selected.id}/grant`, {
        credit_cents: cents,
        points,
        note: grantNote.trim(),
      });
      toast.success(`Updated: $${data.wallet_credits_dollars.toFixed(2)} credits · ${data.loyalty_points} points`);
      // Refresh in place
      setSelected({ ...selected, wallet_credits_cents: data.wallet_credits_cents, loyalty_points: data.loyalty_points });
      setResults(results.map(r => r.id === selected.id ? { ...r, wallet_credits_cents: data.wallet_credits_cents, loyalty_points: data.loyalty_points } : r));
      setGrantCredits(""); setGrantPoints(""); setGrantNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
    } finally {
      setGranting(false);
    }
  };

  // ----- Past-fare attribution (idempotent via server-side dedupe_key) -----
  const submitPastFare = async () => {
    if (!selected) return;
    const amt = parseFloat(pastFareAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    if (!pastFareDate) {
      toast.error("Pick the fare date");
      return;
    }
    setPastFareBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/${selected.id}/past-fares`,
        {
          date: pastFareDate,
          time: pastFareTime || null,
          amount: amt,
          tip: parseFloat(pastFareTip || 0) || 0,
          payment_method: pastFareMethod,
          note: pastFareNote || null,
          driver_id: pastFareDriverId || null,
        }
      );
      if (data.duplicate) {
        toast.info("Already attributed — same date + time + amount + method already on file.");
      } else {
        const pts = data.loyalty?.points_earned || 0;
        const mult = data.loyalty?.multiplier || 1.0;
        const ptsNote = pts > 0 ? ` · +${pts} pts (${mult.toFixed(1)}×)` : "";
        toast.success(`Logged $${amt.toFixed(2)} ${pastFareMethod.toUpperCase()}${ptsNote}`);
        // Reset just the amount + time + note so admin can add the next fare
        // quickly (Christina typically has 2–3 to add in a row).
        setPastFareAmount("");
        setPastFareTip("");
        setPastFareTime("");
        setPastFareNote("");
        // Re-fetch the user so updated lifetime totals reflect the new fare.
        search(selected.email);
        // Also refresh open trip/loyalty panels so they reflect the new row.
        if (tripsOpen) {
          try {
            const { data: tr } = await axios.get(`${API}/admin/customers/${selected.id}/bookings`);
            setTrips(tr);
          } catch { /* non-fatal */ }
        }
        if (loyaltyOpen) {
          try {
            const { data: lh } = await axios.get(`${API}/admin/customers/${selected.id}/loyalty-history`);
            setLoyaltyHistory(lh);
          } catch { /* non-fatal */ }
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to attribute fare");
    } finally {
      setPastFareBusy(false);
    }
  };



  // ----- Link Pro admin grant / revoke -----
  const grantPro = async () => {
    if (!selected) return;
    const days = parseInt(proDays || "0", 10);
    if (!days || days < 1) {
      toast.error("Enter days (1+)");
      return;
    }
    setProBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/${selected.id}/grant-premium`,
        null,
        { params: { days } },
      );
      toast.success(`Granted ${data.plan_name} for ${data.days}d — expires ${new Date(data.expires_at).toLocaleDateString()}`);
      // refresh the selected user row from search
      setSelected({
        ...selected,
        subscription_plan_id: data.plan_id,
        subscription_status: "active",
        subscription_granted_by_admin: true,
        subscription_current_period_end: data.expires_at,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
    } finally {
      setProBusy(false);
    }
  };

  const revokePro = async () => {
    if (!selected) return;
    if (!window.confirm(`Revoke Link Pro from ${selected.email}?`)) return;
    setProBusy(true);
    try {
      await axios.post(`${API}/admin/customers/${selected.id}/revoke-premium`);
      toast.success("Link Pro revoked");
      setSelected({
        ...selected,
        subscription_plan_id: null,
        subscription_status: "canceled",
        subscription_granted_by_admin: false,
        subscription_current_period_end: null,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally {
      setProBusy(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-white/5" data-testid="customer-lookup-card" ref={rootRef}>
      <CardHeader>
        <CardTitle className="text-base font-medium text-white flex items-center gap-2">
          <Search className="w-4 h-4 text-[#4D88FF]" /> Customer Lookup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[11px] text-zinc-500 leading-relaxed -mt-2">
          Search by email/name/phone → tap a result → grant credits, points, or Link Pro.
          You can also tap any row in the &ldquo;All customers&rdquo; list below to jump straight to that user.
        </p>
        {/* Search */}
        <div className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search by email, name, or phone…"
            className="bg-zinc-900 border-white/10 text-white"
            data-testid="lookup-input"
          />
          <Button
            onClick={() => search()}
            disabled={loading || q.trim().length < 2}
            className="bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold"
            data-testid="lookup-search-btn"
          >
            {loading ? "…" : "Search"}
          </Button>
        </div>

        {/* Results */}
        {results.length > 0 && !selected && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {results.map((u) => (
              <button
                key={u.id}
                onClick={() => setSelected(u)}
                data-testid={`lookup-result-${u.id}`}
                className="w-full text-left p-3 rounded-lg bg-zinc-900/50 hover:bg-zinc-900 border border-white/5 hover:border-[#0047FF]/40 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || "(no name)"}
                    </p>
                    <p className="text-xs text-white/50 truncate">{u.email} · {u.phone || "no phone"}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">
                      ${((u.wallet_credits_cents || 0) / 100).toFixed(2)}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">
                      {u.loyalty_points || 0} pts
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Selected user */}
        {selected && (
          <div className="space-y-4 border border-[#0047FF]/30 rounded-xl p-4 bg-gradient-to-br from-[#0047FF]/10 to-transparent">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-white">
                  {[selected.first_name, selected.last_name].filter(Boolean).join(" ") || "(no name)"}
                </p>
                <p className="text-xs text-white/60 truncate">{selected.email} · {selected.phone || "no phone"}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-white/70 font-bold">{selected.role}</span>
                  {selected.subscription_plan_id && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold flex items-center gap-1">
                      <Crown className="w-3 h-3" /> {selected.subscription_plan_id} · {selected.subscription_status}
                    </span>
                  )}
                  {selected.locked_pricing && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Price Locked
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="lookup-close-btn"
                className="text-white/40 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
                <Wallet className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                <p className="text-2xl font-black text-amber-300" style={{ fontFamily: "Outfit, system-ui" }}>
                  ${((selected.wallet_credits_cents || 0) / 100).toFixed(2)}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Wallet</p>
              </div>
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3">
                <Sparkles className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                <p className="text-2xl font-black text-emerald-300" style={{ fontFamily: "Outfit, system-ui" }}>
                  {selected.loyalty_points || 0}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Points</p>
              </div>
            </div>

            <CreditsAuditPanel
              key={selected.id}
              userId={selected.id}
              onUpdated={(a) => setSelected((s) => ({ ...s, wallet_credits_cents: a.wallet_credits_cents, loyalty_points: a.loyalty_points }))}
            />

            {/* Loyalty earn history — lazy-loaded on toggle */}
            <div className="pt-2" data-testid="loyalty-history-section">
              <button
                type="button"
                onClick={async () => {
                  const next = !loyaltyOpen;
                  setLoyaltyOpen(next);
                  if (next && !loyaltyHistory) {
                    setLoyaltyBusy(true);
                    try {
                      const { data } = await axios.get(`${API}/admin/customers/${selected.id}/loyalty-history`);
                      setLoyaltyHistory(data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Could not load history");
                    } finally {
                      setLoyaltyBusy(false);
                    }
                  }
                }}
                className="w-full text-left text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-emerald-500/5"
                data-testid="loyalty-history-toggle"
              >
                <History className="w-3 h-3" />
                {loyaltyOpen ? "Hide" : "Show"} points earn history
                {loyaltyHistory && (
                  <span className="ml-auto text-zinc-500">
                    {loyaltyHistory.history?.length || 0} trip{(loyaltyHistory.history?.length || 0) === 1 ? '' : 's'}
                  </span>
                )}
              </button>
              {loyaltyOpen && (
                <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="loyalty-history-list">
                  {loyaltyBusy && (
                    <p className="text-[11px] text-zinc-500 text-center py-3">Loading…</p>
                  )}
                  {!loyaltyBusy && loyaltyHistory && loyaltyHistory.history?.length === 0 && (
                    <p className="text-[11px] text-zinc-500 text-center py-3">
                      No points history yet — points are credited on trip completion.
                    </p>
                  )}
                  {!loyaltyBusy && loyaltyHistory && loyaltyHistory.history?.length > 0 && (
                    <>
                      {loyaltyHistory.is_active_pro && (
                        <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 mb-1.5 flex items-center gap-1.5">
                          <Crown className="w-3 h-3" /> Currently earning {loyaltyHistory.current_multiplier}× as Link Pro
                        </div>
                      )}
                      {loyaltyHistory.history.map((h) => (
                        <div
                          key={h.booking_id}
                          className="text-[11px] flex items-center justify-between gap-2 bg-black/30 rounded p-2 border border-white/5"
                          data-testid={`loyalty-history-row-${h.booking_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-bold">+{h.points_earned_estimate} pts <span className="text-zinc-500 font-normal">· ${h.subtotal.toFixed(2)} × {h.multiplier_used_estimate}</span></p>
                            <p className="text-zinc-500 truncate">
                              {h.completed_at?.slice(0, 10)} · {h.pickup?.slice(0, 22)}{h.pickup?.length > 22 ? '…' : ''} → {h.dropoff?.slice(0, 22)}{h.dropoff?.length > 22 ? '…' : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                      <p className="text-[10px] text-zinc-500 text-center pt-1">
                        Estimated total from trips: <span className="font-bold text-emerald-300">{loyaltyHistory.total_from_trips_estimate} pts</span> · Wallet balance: <span className="font-bold text-emerald-300">{loyaltyHistory.loyalty_points} pts</span>
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Trips section — lazy-loaded with per-row delete */}
            <div className="pt-2" data-testid="trips-section">
              <button
                type="button"
                onClick={async () => {
                  const next = !tripsOpen;
                  setTripsOpen(next);
                  if (next && !trips) {
                    setTripsBusy(true);
                    try {
                      const { data } = await axios.get(`${API}/admin/customers/${selected.id}/bookings`);
                      setTrips(data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Could not load trips");
                    } finally {
                      setTripsBusy(false);
                    }
                  }
                }}
                className="w-full text-left text-[11px] text-blue-300 hover:text-blue-200 flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-blue-500/5"
                data-testid="trips-toggle"
              >
                <UserIcon className="w-3 h-3" />
                {tripsOpen ? "Hide" : "Show"} recent trips
                {trips && (
                  <span className="ml-auto text-zinc-500">
                    {trips.count} trip{trips.count === 1 ? '' : 's'}
                  </span>
                )}
              </button>
              {tripsOpen && (
                <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="trips-list">
                  {tripsBusy && <p className="text-[11px] text-zinc-500 text-center py-3">Loading…</p>}
                  {!tripsBusy && trips && trips.bookings.length === 0 && (
                    <p className="text-[11px] text-zinc-500 text-center py-3">No trips on file.</p>
                  )}
                  {!tripsBusy && trips && trips.bookings.map((b) => (
                    <div
                      key={b.id}
                      className="text-[11px] flex items-center justify-between gap-2 bg-black/30 rounded p-2 border border-white/5"
                      data-testid={`trip-row-${b.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-bold">
                          ${(b.estimated_price || 0).toFixed(2)}
                          <span className="ml-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-normal">{b.status}</span>
                          {!b.driver_id && (
                            <span className="ml-1 text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">no driver</span>
                          )}
                        </p>
                        <p className="text-zinc-500 truncate">
                          {(b.completed_at || b.created_at || '').slice(0, 10)} · {b.pickup_address?.slice(0, 20)}{b.pickup_address?.length > 20 ? '…' : ''} → {b.dropoff_address?.slice(0, 20)}{b.dropoff_address?.length > 20 ? '…' : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={tripDeletingId === b.id}
                        onClick={async () => {
                          if (!window.confirm(
                            `Delete this trip permanently?\n\n` +
                            `${b.pickup_address?.slice(0, 40)} → ${b.dropoff_address?.slice(0, 40)}\n` +
                            `$${(b.estimated_price || 0).toFixed(2)} · ${b.status}\n\n` +
                            `The customer's profile, plan, wallet, and other trips are untouched. Tied revenue events are removed so income totals stay accurate. This can't be undone.`
                          )) return;
                          setTripDeletingId(b.id);
                          try {
                            await axios.delete(`${API}/admin/bookings/${b.id}`);
                            // Refresh trips list
                            const { data } = await axios.get(`${API}/admin/customers/${selected.id}/bookings`);
                            setTrips(data);
                            // Also refresh loyalty history if it's open
                            if (loyaltyOpen) {
                              try {
                                const { data: lh } = await axios.get(`${API}/admin/customers/${selected.id}/loyalty-history`);
                                setLoyaltyHistory(lh);
                              } catch (err) { /* non-fatal */ }
                            }
                            toast.success(`Trip deleted. ${data.count} trip${data.count === 1 ? '' : 's'} remaining.`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Delete failed");
                          } finally {
                            setTripDeletingId(null);
                          }
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded p-1.5 disabled:opacity-40 shrink-0"
                        title="Delete this single trip — customer profile and plan untouched"
                        data-testid={`delete-trip-${b.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Link Pro admin grant / revoke */}
            <div className="space-y-2 pt-3 border-t border-white/10" data-testid="link-pro-admin-section">
              <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70 font-bold flex items-center gap-1.5">
                <Crown className="w-3 h-3" /> Link Pro · admin grant (no Stripe charge)
              </p>
              {(() => {
                const isGranted = selected.subscription_granted_by_admin && selected.subscription_status === "active";
                const isPaid = !!selected.stripe_subscription_id;
                if (isPaid) {
                  return (
                    <p className="text-xs text-amber-200 bg-amber-500/5 border border-amber-500/20 rounded p-2">
                      This user has a paid Stripe subscription — manage via Stripe portal, not here.
                    </p>
                  );
                }
                return (
                  <>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-zinc-400 text-xs">Days</Label>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          value={proDays}
                          onChange={(e) => setProDays(e.target.value)}
                          placeholder="30"
                          className="bg-zinc-900 border-white/10 text-white mt-1"
                          data-testid="link-pro-days-input"
                        />
                      </div>
                      <Button
                        onClick={grantPro}
                        disabled={proBusy}
                        className="bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg"
                        data-testid="link-pro-grant-btn"
                      >
                        {proBusy ? "…" : isGranted ? "Extend" : "Grant"}
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {[7, 30, 90, 365].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setProDays(String(d))}
                          className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white font-bold"
                          data-testid={`link-pro-quick-${d}`}
                        >
                          {d}d
                        </button>
                      ))}
                    </div>
                    {isGranted && (
                      <div className="flex items-center justify-between gap-2 mt-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                        <p className="text-[11px] text-amber-200">
                          Active comp · expires {selected.subscription_current_period_end ? new Date(selected.subscription_current_period_end).toLocaleDateString() : "—"}
                        </p>
                        <Button
                          onClick={revokePro}
                          disabled={proBusy}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2 text-xs"
                          data-testid="link-pro-revoke-btn"
                        >
                          Revoke
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Grant form */}
            <div className="space-y-3 pt-2 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Manual grant · use negative to deduct</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Add Credit ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={grantCredits}
                    onChange={(e) => setGrantCredits(e.target.value)}
                    placeholder="e.g. 25.00"
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="grant-credits"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Add Points</Label>
                  <Input
                    type="number"
                    step="1"
                    value={grantPoints}
                    onChange={(e) => setGrantPoints(e.target.value)}
                    placeholder="e.g. 100"
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="grant-points"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Note (audit log)</Label>
                <Input
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="e.g. Refund for failed delivery"
                  className="bg-zinc-900 border-white/10 text-white mt-1"
                  data-testid="grant-note"
                />
              </div>
              <Button
                onClick={submitGrant}
                disabled={granting}
                className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-lg"
                data-testid="grant-submit-btn"
              >
                {granting ? "Applying…" : "Apply Grant"}
              </Button>
            </div>

            {/* Past-fare attribution — for JIM card / cash sales that cleared
                outside Link Ride and need to be added to this customer's totals.
                Idempotent: same date+amount+method won't double-count. */}
            <div className="space-y-3 pt-3 border-t border-white/10" data-testid="past-fare-section">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 font-bold">Add past fare · JIM / cash / etc.</p>
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Attach a fare that already cleared in an outside system. Re-submitting the same date + amount + method is a no-op (no duplicate row).
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Date</Label>
                  <Input
                    type="date"
                    value={pastFareDate}
                    onChange={(e) => setPastFareDate(e.target.value)}
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="past-fare-date"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Time (optional)</Label>
                  <Input
                    type="time"
                    value={pastFareTime}
                    onChange={(e) => setPastFareTime(e.target.value)}
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="past-fare-time"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pastFareAmount}
                    onChange={(e) => setPastFareAmount(e.target.value)}
                    placeholder="48.48"
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="past-fare-amount"
                  />
                  <p className="text-[10px] text-zinc-500 mt-0.5">Fare with tax · excludes tip</p>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Tip ($) <span className="text-zinc-600">— optional</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pastFareTip}
                    onChange={(e) => setPastFareTip(e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="past-fare-tip"
                  />
                  <p className="text-[10px] text-zinc-500 mt-0.5">Non-taxable · 100% to driver</p>
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Payment method</Label>
                <select
                  value={pastFareMethod}
                  onChange={(e) => setPastFareMethod(e.target.value)}
                  className="w-full bg-zinc-900 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
                  data-testid="past-fare-method"
                >
                  <option value="jim_card">JIM card</option>
                  <option value="cash">Cash</option>
                  <option value="stripe">Stripe (manual)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Driver (credit earnings + 1099)</Label>
                <select
                  value={pastFareDriverId}
                  onChange={(e) => setPastFareDriverId(e.target.value)}
                  className="w-full bg-zinc-900 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
                  data-testid="past-fare-driver"
                >
                  <option value="">— No driver (won&apos;t roll into earnings) —</option>
                  {approvedDrivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {(d.first_name || "") + " " + (d.last_name || "")} {d.email ? `· ${d.email}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Note (optional)</Label>
                <Input
                  value={pastFareNote}
                  onChange={(e) => setPastFareNote(e.target.value)}
                  placeholder="e.g. JIM tx 0511A"
                  className="bg-zinc-900 border-white/10 text-white mt-1"
                  data-testid="past-fare-note"
                />
              </div>
              <Button
                onClick={submitPastFare}
                disabled={pastFareBusy}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg"
                data-testid="past-fare-submit-btn"
              >
                {pastFareBusy ? "Attributing…" : `Attribute fare · $${parseFloat(pastFareAmount || 0).toFixed(2)}`}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
